import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import useCanonicalUrl from '@/hooks/useCanonicalUrl';
import Layout from '@/components/layout/Layout';

const TermsPage = () => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // ============ PRIORITY 2: ADD CANONICAL URL ============
  useCanonicalUrl();

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true);
        const { getApiUrl } = await import('../config');
        const response = await fetch(getApiUrl('/public/legal/terms_of_service'));
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.data && data.data.content) {
          setContent(data.data.content);
        } else {
          setError('Failed to load terms and conditions');
        }
      } catch (err) {
        console.error('Error fetching terms:', err);
        setError('Unable to load terms and conditions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Terms & Conditions | Virewire Property Services</title>
        <meta name="description" content="Read our terms and conditions for using Virewire property services." />
      </Helmet>

      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-8">Terms & Conditions</h1>
            
            {loading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3 items-start mb-6">
                <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-medium">Error</p>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}
            
            {content && (
              <div className="prose prose-lg max-w-none">
                <div dangerouslySetInnerHTML={{ __html: content }} />
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TermsPage;
